// ANY EDITS TO THIS PAGE SHOULD ALSO BE MADE TO ITS DEMO COUNTERPART, TO KEEP THE APP AND DEMO IN SYNC
import './TryoutPage.css';

import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  doc,
  getDoc,
  getFirestore,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import { usePostHog } from '@posthog/react';

import ConfirmDialog from '../components/common/ConfirmDialog/ConfirmDialog';
import CopyIcon from '../components/common/CopyIcon';
import VideoUploadNavIndicator
  from '../components/common/VideoUploadNavIndicator/VideoUploadNavIndicator';
import { useScoringAuth } from '../contexts/ScoringAuthContext';
import { scoringApp } from '../firebase';
import { copyTextToClipboard } from '../utils/clipboard';
import {
  ChevronRightIcon,
  ClipboardIcon,
  ExportIcon,
  LeaveIcon,
  MegaphoneIcon,
  PlusIcon,
  RankIcon,
  SettingsIcon,
} from './TryoutIcons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DashboardState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      tryoutName: string;
      code: string;
      coachName: string;
      gymLogoUrl: string | null;
    };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TryoutPage: React.FC = () => {
  const { tryoutId } = useParams<{ tryoutId: string }>();
  const navigate = useNavigate();
  const { scoringUser, scoringLogout } = useScoringAuth();
  const posthog = usePostHog();
  const db = getFirestore(scoringApp);

  const [dashState, setDashState] = useState<DashboardState>({ status: 'loading' });
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [tryoutLogoLoaded, setTryoutLogoLoaded] = useState(false);
  const [showTryoutLogoSkeleton, setShowTryoutLogoSkeleton] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // useEffect is appropriate here: fetching data based on route params.
  useEffect(() => {
    if (!tryoutId || !scoringUser) {
      setDashState({ status: 'error', message: 'Invalid session.' });
      return;
    }

    let cancelled = false;
    const coachUid = scoringUser.uid;

    async function load(): Promise<void> {
      const [tryoutSnap, coachSnap] = await Promise.all([
        getDoc(doc(db, 'tryouts', tryoutId!)),
        getDoc(doc(db, 'tryouts', tryoutId!, 'coaches', coachUid)),
      ]);

      if (cancelled) return;

      if (!tryoutSnap.exists()) {
        setDashState({ status: 'error', message: 'Tryout not found.' });
        return;
      }

      const data = tryoutSnap.data() as Record<string, unknown>;
      const tryoutName = typeof data.name === 'string' ? data.name : '';
      const code = typeof data.code === 'string' ? data.code : '';
      const rawLogoUrl = data.gymLogoUrl;
      const gymLogoUrl =
        typeof rawLogoUrl === 'string' && rawLogoUrl.trim().length > 0
          ? rawLogoUrl.trim()
          : null;

      const coachData = coachSnap.exists()
        ? (coachSnap.data() as Record<string, unknown>)
        : null;
      const coachName =
        typeof coachData?.name === 'string' ? coachData.name : 'Coach';

      setDashState({ status: 'ready', tryoutName, code, coachName, gymLogoUrl });
    }

    load().catch((err: unknown) => {
      console.error('[TryoutPage] load failed:', err);
      if (!cancelled) {
        const detail = err instanceof Error ? ` (${err.message})` : '';
        setDashState({ status: 'error', message: `Failed to load tryout.${detail}` });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [db, tryoutId, scoringUser]);

  const currentGymLogoUrl = dashState.status === 'ready' ? dashState.gymLogoUrl : null;

  useEffect(() => {
    setTryoutLogoLoaded(false);
    setShowTryoutLogoSkeleton(false);
  }, [currentGymLogoUrl]);

  useEffect(() => {
    if (!currentGymLogoUrl || tryoutLogoLoaded) {
      setShowTryoutLogoSkeleton(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowTryoutLogoSkeleton(true);
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [currentGymLogoUrl, tryoutLogoLoaded]);

  useEffect(() => {
    const src = currentGymLogoUrl?.trim();
    if (!src) return;

    let cancelled = false;
    const probe = new Image();

    const markLoaded = (): void => {
      if (!cancelled) setTryoutLogoLoaded(true);
    };

    probe.onload = markLoaded;
    probe.onerror = () => {};
    probe.src = src;

    if (probe.complete && probe.naturalWidth > 0) {
      markLoaded();
    } else if (typeof probe.decode === 'function') {
      void probe.decode().then(markLoaded).catch(() => {});
    }

    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [currentGymLogoUrl]);

  const handleTryoutLogoLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>): void => {
    const img = e.currentTarget;
    if (typeof img.decode === 'function') {
      void img.decode().catch(() => {}).finally(() => {
        setTryoutLogoLoaded(true);
      });
      return;
    }
    setTryoutLogoLoaded(true);
  }, []);

  const handleTryoutLogoError = useCallback((): void => {
    setTryoutLogoLoaded(true);
  }, []);

  const handleLeaveConfirm = (): void => {
    setLeaving(true);
    posthog?.capture('tryout_left', { tryout_id: tryoutId });
    scoringLogout()
      .catch(() => {})
      .finally(() => navigate('/join'));
  };

  const copyTimeoutRef = React.useRef<number | null>(null);

  const handleCopyCode = useCallback((code: string): void => {
    void copyTextToClipboard(code).then(() => {
      posthog?.capture('tryout_code_copied', { tryout_id: tryoutId });
      setCodeCopied(true);
      if (copyTimeoutRef.current != null) window.clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = window.setTimeout(() => {
        setCodeCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    });
  }, [posthog, tryoutId]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const logo = `${process.env.PUBLIC_URL}/logo.svg`;

  if (dashState.status === 'loading') {
    return (
      <div className="tryout-shell">
        <header className="tryout-nav">
          <img src={logo} alt="CheerEval" className="tryout-nav__logo" />
          <div className="tryout-nav__code-chip tryout-nav__code-chip--skeleton" aria-hidden="true" />
        </header>
        <div className="tryout-scroll">
          <section className="tryout-section">
            <div className="tryout-context-card">
              <div className="tryout-skeleton tryout-skeleton--logo-sq" />
              <div className="tryout-skeleton-text-col">
                <div className="tryout-skeleton tryout-skeleton--title" />
                <div className="tryout-skeleton tryout-skeleton--badge" />
              </div>
            </div>
          </section>
          <section className="tryout-section">
            <div className="tryout-skeleton tryout-skeleton--score-block" />
          </section>
          <section className="tryout-section tryout-grid">
            <div className="tryout-skeleton tryout-skeleton--grid-cell" />
            <div className="tryout-skeleton tryout-skeleton--grid-cell" />
          </section>
          <div className="tryout-leave-row">
          </div>
        </div>
      </div>
    );
  }

  if (dashState.status === 'error') {
    return (
      <div className="tryout-shell">
        <header className="tryout-nav">
          <img src={logo} alt="CheerEval" className="tryout-nav__logo" />
        </header>
        <div className="tryout-scroll">
          <div className="tryout-error-card">
            <p className="tryout-error-msg">{dashState.message}</p>
            <button
              type="button"
              className="tryout-score-block"
              onClick={() => navigate('/join')}
            >
              Back to Join
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { tryoutName, code, coachName, gymLogoUrl } = dashState;

  return (
    <>
      <div className="tryout-shell">
        {/* ── Nav bar ──────────────────────────────────────────── */}
        <header className="tryout-nav">
          <img src={logo} alt="CheerEval" className="tryout-nav__logo" />

          <div className="tryout-nav__right-col">
            <button
              type="button"
              className="tryout-nav__code-chip"
              onClick={() => handleCopyCode(code)}
              aria-label={`Copy tryout code ${code}`}
            >
              <span className="tryout-nav__code-label">{codeCopied ? 'Copied!' : 'Code'}</span>
              <span className="tryout-nav__code-value">{code}</span>
              <CopyIcon className="tryout-nav__copy-icon" />
            </button>
            <VideoUploadNavIndicator />
          </div>
        </header>

        <div className="tryout-scroll">
          {/* ── Context card ─────────────────────────────────── */}
          <section className="tryout-section">
            <div className="tryout-context-card">
              {gymLogoUrl ? (
                <div className="tryout-context-card__logo-wrap">
                  {showTryoutLogoSkeleton && (
                    <div className="tryout-skeleton tryout-skeleton--logo-sq" aria-hidden="true" />
                  )}
                  <img
                    src={gymLogoUrl}
                    alt={`${tryoutName} gym logo`}
                    className={[
                      'tryout-context-card__logo',
                      tryoutLogoLoaded ? 'tryout-context-card__logo--visible' : '',
                    ].filter(Boolean).join(' ')}
                    onLoad={handleTryoutLogoLoad}
                    onError={handleTryoutLogoError}
                    loading="eager"
                    decoding="async"
                  />
                </div>
              ) : null}

              <div className="tryout-context-card__info">
                <div className="tryout-context-card__tryout-name">{tryoutName}</div>
                <div className="tryout-context-card__live-badge">
                  Welcome, {coachName}
                </div>
              </div>
            </div>
          </section>

          {/* ── Score Athletes — big press block ─────────────── */}
          <section className="tryout-section">
            <button
              type="button"
              className="tryout-score-block"
              onClick={() => navigate(`/tryout/${tryoutId}/athletes`)}
            >
              <div className="tryout-score-block__header">
                <div className="tryout-score-block__label">
                  Evaluate<br />Athletes
                </div>
                <div className="tryout-score-block__icon">
                  <ClipboardIcon size={22} />
                </div>
              </div>
              <div className="tryout-score-block__footer">
                <div className="tryout-score-block__arrow">
                  <ChevronRightIcon size={20} />
                </div>
              </div>
            </button>
          </section>

          {/* ── 2-up grid: Rank / Add ─────────────────────────── */}
          <section className="tryout-section tryout-grid">
            <button
              type="button"
              className="tryout-grid-cell"
              onClick={() => navigate(`/tryout/${tryoutId}/ranking`)}
            >
              <div className="tryout-grid-cell__icon">
                <RankIcon size={22} />
              </div>
              <div>
                <div className="tryout-grid-cell__label">Place Athletes</div>
              </div>
            </button>

            <button
              type="button"
              className="tryout-grid-cell"
              onClick={() => navigate(`/tryout/${tryoutId}/add`)}
            >
              <div className="tryout-grid-cell__icon">
                <PlusIcon size={22} />
              </div>
              <div>
                <div className="tryout-grid-cell__label">Add Athletes</div>
              </div>
            </button>
          </section>
          <section className="tryout-section tryout-grid">
            <button
              type="button"
              className="tryout-grid-cell"
              onClick={() => navigate(`/tryout/${tryoutId}/announcements`)}
            >
              <div className="tryout-grid-cell__icon">
                <MegaphoneIcon size={20} strokeWidth={2} />
              </div>
              <div>
                <div className="tryout-grid-cell__label">Announcements</div>
              </div>
            </button>
            <button
              type="button"
              className="tryout-grid-cell"
              onClick={() => navigate(`/tryout/${tryoutId}/export`)}
            >
              <div className="tryout-grid-cell__icon">
                <ExportIcon size={20} strokeWidth={2} />
              </div>
              <div>
                <div className="tryout-grid-cell__label">Export Athletes</div>
              </div>
            </button>
            <button
              type="button"
              className="tryout-grid-cell"
              onClick={() => navigate(`/tryout/${tryoutId}/settings`)}
            >
              <div className="tryout-grid-cell__icon">
                <SettingsIcon size={22} strokeWidth={2} />
              </div>
              <div>
                <div className="tryout-grid-cell__label">Settings</div>
              </div>
            </button>
          </section>

          {/* ── Leave Tryout ────────────────── */}
          <div className="tryout-leave-row">
            <button
              type="button"
              className="tryout-leave-btn"
              onClick={() => setLeaveOpen(true)}
              disabled={leaving}
            >
              <LeaveIcon size={14} />
              Leave tryout
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={leaveOpen}
        title="Leave Tryout"
        message="Are you sure you want to leave?"
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={handleLeaveConfirm}
        onCancel={() => setLeaveOpen(false)}
      />
    </>
  );
};

export default TryoutPage;
